import React, { useState, useEffect, useContext } from "react";

import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import Tippy from "@tippyjs/react";
import { Trans, useTranslation } from "react-i18next";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import CardTable from "../../components/Tables/CardTable";
import Pagination from "../../components/Pagination/Pagination";
import UsersAPI from "../../API/users.api";
import LookupsAPI from "../../API/lookups.api";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getRoles } from "../../utils/OktaHelpers";
import { ROLES } from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getSessionFilters, getMappedFilters } from "../../utils/FilterHelpers";
import UserContext from "../../lib/UserContext";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { setObject, getObject } from "../../utils/StorageHelpers";
import MainLoadingButton from "../../components/Buttons/MainLoadingButton";
import { mapLookupToSelect } from "../../utils/Helpers";
import MissingData from "../../components/Shared/MissingData";
import NestedTable from "../../components/Tables/NestedTable";
import DeleteButton from "../../components/Buttons/DeleteButton";
import EditButton from "../../components/Buttons/EditButton";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import { longDateTime } from "../../utils/DateHelper";

const User = () => {
    const [users, setUsers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRendered, setIsRendered] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [filters, setFilters] = useState({});
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("customer-table-filters")
    );
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [isDeletingUser, setIsDeletingUser] = useState(false);
    const { t } = useTranslation();
    const context = useContext(UserContext);

    const authRoles = [ROLES.partnerAdmin, ROLES.support, ROLES.superAdmin];
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Users-itemsPerPage", true) || 10
    );

    //SuperAdmin Actions
    const [deleteButton, setDeleteButton] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(false);

    const keys = [
        { name: "firstname" },
        { name: "lastname" },
        {
            name: "email",
            element: (row) => {
                return row.email ? (
                    <Tippy
                        content={row.email}
                        placement="top"
                        animation="scale"
                    >
                        <span className="w-40 inline">{row.email}</span>
                    </Tippy>
                ) : (
                    "---"
                );
            },
        },
        { name: "organization", role: !isAuthorized(authRoles, getRoles()) },
        { name: "role", role: !isAuthorized(authRoles, getRoles()) },
        {
            name: "admin",
            element: (row) => {
                return row.admin ? (
                    <Tippy
                        content={row.admin}
                        placement="top"
                        animation="scale"
                    >
                        <span
                            tabIndex="0"
                            className="w-40 overflow-hidden block overflow-ellipsis whitespace-nowrap"
                        >
                            {row.admin}
                        </span>
                    </Tippy>
                ) : (
                    "---"
                );
            },
            role: !isAuthorized(authRoles, getRoles()),
        },
        {
            name: "actions",
            element: (row, index, mainRows, mainIndex) => (
                <div className="flex items-center justify-center">
                    <DeleteButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setDeleteButton(true);
                            setSelectedIndex(mainIndex);
                        }}
                        tippy={true}
                    />

                    <Link
                        to={{
                            pathname: "/users/form",
                            state: {
                                data: users[mainIndex],
                            },
                        }}
                    >
                        <EditButton tippy={true} />
                    </Link>
                </div>
            ),
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
    ];

    const subKeys = [
        {
            name: "name",
        },
        { name: "role" },
        {
            name: "lastUpdatedAt",
            element: (row) =>
                row.lastUpdatedAt ? longDateTime(row.lastUpdatedAt) : "---",
        },
    ];

    const headers = [
        { label: t("firstName"), name: "firstname" },
        { label: t("lastName"), name: "lastname" },
        { label: t("email"), name: "email" },
        {
            label: t("affiliate"),
            name: "organization",
            role: !isAuthorized(authRoles, getRoles()),
        },
        {
            label: t("role"),
            name: "role",
            role: !isAuthorized(authRoles, getRoles()),
        },
        {
            label: t("admin"),
            name: "admin",
            role: !isAuthorized(authRoles, getRoles()),
        },
        { label: t("actions"), skipSort: true },
    ];
    const subHeaders = [
        { label: t("affiliate"), name: "affiliate", skipSort: true },
        { label: t("role"), name: "role", skipSort: true },
        { label: t("lastModified"), name: "lastModified", skipSort: true },
    ];
    const searchFilters = [
        {
            label: t("firstName"),
            name: "firstname",
            type: "Input",
            placeholder: t("firstNamePlaceholder"),
        },
        {
            label: t("lastName"),
            name: "lastname",
            type: "Input",
            placeholder: t("lastNamePlaceholder"),
        },
        {
            label: t("email"),
            name: "email",
            type: "Input",
            placeholder: t("emailPlaceholder"),
        },
        {
            label: t("affiliate"),
            name: "organizationId",
            type: "Dropdown",
            options: organizations,
            disabled: !isAuthorized(authRoles, getRoles()),
        },
    ];

    useEffect(() => {
        const localStorageFilters = getSessionFilters("customer-table-filters");

        if (!isEmpty(localStorageFilters)) {
            setFilters(localStorageFilters);
            setAppliedFilters(localStorageFilters);
        }

        const _user = context.getUser();
        if (isAuthorized(authRoles, [_user.role])) {
            fetchOrganizations();
        }
        setIsRendered(true);
    }, []);

    useEffect(() => {
        if (isRendered) {
            if (sortBy && sortDirection) {
                setCurrentPage(1);
                fetchData(appliedFilters, sortBy, sortDirection);
            } else {
                setCurrentPage(1);
                fetchData(appliedFilters);
            }
        }
    }, [sortBy, sortDirection, isRendered]);

    const applyFilters = (retrievedApplyFilters) => {
        fetchData(retrievedApplyFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const fetchData = async (
        _filters = null,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const newFilter = getMappedFilters(_filters);
        const _offset = offset < 0 ? 0 : offset; // to ensure you don't get a negative offset

        let result = await UsersAPI.getUsers(
            itemsPerPage,
            newFilter,
            _offset,
            _sortBy,
            _sortDirection
        );

        setUsers(mapUsers(result?.data || []));
        setTotalCount(result?.totalCount || 0);
        setIsLoading(false);
    };

    const fetchOrganizations = async () => {
        let result = await LookupsAPI.getOrganizations();
        if (result) {
            setOrganizations(mapLookupToSelect(result));
        }
    };

    const mapUsers = (data) => {
        let mapped = [];
        if (data && data.length > 0) {
            mapped = data.map((user) => {
                return {
                    id: user.id,
                    firstname: user.firstname,
                    lastname: user.lastname,
                    email: user.email,
                    organization: user.organization?.name,
                    organizationId: user.organizationId,
                    role: user.role,
                    admin: user.admin?.email,
                    image: user.image,
                    ...(isAuthorized([ROLES.superAdmin], getRoles()) && {
                        userOrganizations: [
                            { ...user.organization, role: user.role },
                            ...user.userOrganizations,
                        ],
                    }),
                };
            });
        }

        return mapped;
    };

    const handleDelete = async (index) => {
        try {
            setIsDeletingUser(true);
            const result = await UsersAPI.deleteUser(users[index].id);
            if (!isEmpty(result)) {
                SuccessAlert(t("deleteUserSuccessMessage"));
                LogCapture.info(
                    `User deleted with email: ${users[index]?.email}`
                );
                let newCurrentPage = currentPage;
                if (users.length <= 1 && currentPage > 1) {
                    newCurrentPage = currentPage - 1;
                }

                setCurrentPage(newCurrentPage);
                fetchData(filters, sortBy, sortDirection, newCurrentPage - 1);
                return true;
            } else {
                // ErrorAlert(t("deleteUserFailMessage"));
                LogCapture.error(
                    `Couldn't delete user with email: ${users[index]?.email}`
                );
                return false;
            }
        } catch (error) {
            console.log(error);
            LogCapture.error(
                `Couldn't delete user with email: ${users[index]?.email}, error: ${error}`
            );
            ErrorAlert(t("deleteUserFailMessage"));
        } finally {
            setIsDeletingUser(false);
        }
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            // call API to get users with the next pagination
            fetchData(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Users-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchData(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    const renderSuperAdminTable = () => {
        return (
            <NestedTable
                keys={keys}
                headers={headers}
                subHeaders={subHeaders}
                rows={users}
                subKeys={subKeys}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                totalCount={totalCount}
                sizePerPage={itemsPerRequest}
                handleItemsPerPage={handleItemsPerPage}
                isFilterUsed={filtersUsed}
                onPageChange={handlePageClick}
                currentPage={currentPage}
                purpose="users"
                subRowsKey="userOrganizations"
                includeMainRowImage={true}
            />
        );
    };

    const renderTable = () => {
        return (
            <div className="card-table shadow">
                <CardTable
                    keys={keys}
                    headers={headers}
                    rows={users}
                    handleDelete={handleDelete}
                    purpose={"users"}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    actionColumn={true}
                    isSubmitting={isDeletingUser}
                />
                <Pagination
                    currentPage={currentPage}
                    setCurrentPage={handlePageClick}
                    totalCount={totalCount}
                    sizePerPage={itemsPerRequest}
                    handleItemsPerPage={handleItemsPerPage}
                />
            </div>
        );
    };

    return (
        <>
            <div id="customer-content" className="customer-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="headerUser">Users</Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={searchFilters}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component="customer-table"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                        />
                        <Link to="/users/form">
                            <MainLoadingButton
                                buttonText={t("addNewUser")}
                                iconClassName="fas fa-plus"
                                className="ml-2"
                            />
                        </Link>
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading} text="Loading...">
                {users.length > 0 ? (
                    <>
                        {/* <TableLoader isLoading={isLoading} /> */}
                        {isAuthorized([ROLES.superAdmin], getRoles())
                            ? renderSuperAdminTable()
                            : renderTable()}
                    </>
                ) : (
                    <MissingData
                        isFilterUsed={filtersUsed}
                        entity={t("headerUser")}
                        includeText={!isLoading}
                        path="/users/form"
                    />
                )}
            </LoadingOverlay>

            <BaseAlertModal
                isVisible={deleteButton}
                setIsVisible={setDeleteButton}
                title={t("deleteTitle", { purpose: "users" })}
                message={t("deleteMessage", { purpose: "users" })}
                action={{ submitAction: () => handleDelete(selectedIndex) }}
                type="delete"
                isSubmitting={isDeletingUser}
            />
        </>
    );
};

export default User;
